import { useEffect, useRef, useState } from "react";
import { ButtonProps } from "@mui/material";
import { copyToClipboard } from "~/lib/clipboard";
import hljs from "highlight.js/lib/core";
import bash from "highlight.js/lib/languages/bash";
import ini from "highlight.js/lib/languages/ini";
import javascript from "highlight.js/lib/languages/javascript";
import plaintext from "highlight.js/lib/languages/plaintext";
import shell from "highlight.js/lib/languages/shell";
import yaml from "highlight.js/lib/languages/yaml";
import diff from "highlight.js/lib/languages/diff";
import json from "highlight.js/lib/languages/json";
import powershell from "highlight.js/lib/languages/powershell";
import dockerfile from "highlight.js/lib/languages/dockerfile";
import dns from "highlight.js/lib/languages/dns";

hljs.registerLanguage("bash", bash);
hljs.registerLanguage("ini", ini);
hljs.registerLanguage("javascript", javascript);
hljs.registerLanguage("plaintext", plaintext);
hljs.registerLanguage("shell", shell);
hljs.registerLanguage("yaml", yaml);
hljs.registerLanguage("diff", diff);
hljs.registerLanguage("json", json);
hljs.registerLanguage("powershell", powershell);
hljs.registerLanguage("dockerfile", dockerfile);
hljs.registerLanguage("dns", dns);

hljs.configure({
  languages: [
    "bash",
    "ini",
    "javascript",
    "plaintext",
    "shell",
    "yaml",
    "diff",
    "json",
    "powershell",
    "dockerfile",
    "dns",
  ],
  ignoreUnescapedHTML: true,
});

type UseCodeProps = {
  className: string | undefined;
  languages?: string[];
};

export function useCode({ className, languages = [] }: UseCodeProps) {
  const elRef = useRef<HTMLPreElement>(null);
  const [justCopied, setJustCopied] = useState<boolean>(false);

  useEffect(() => {
    if (elRef.current) {
      highlightCode();
    }
    // Run once on mount to avoid re-highlighting on every render,
    // which would reset any active text selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const highlightCode = () => {
    if (!elRef.current) {
      console.error("cannot highlight code, no element given");
      return;
    }

    const nodes = elRef.current.querySelectorAll<HTMLElement>(":scope > code");

    if (languages.length === 0 && className) {
      languages.push(className);
    }

    nodes.forEach((node) => {
      node.removeAttribute("data-highlighted");
      hljs.highlightElement(node);
    });
  };

  const handleCopyToClipboard: ButtonProps["onClick"] = async (e) => {
    e.stopPropagation();

    if (!elRef.current) {
      throw new Error(
        "Cannot copy code to clipboard because element does not exist in the tree.",
      );
    }

    try {
      await copyToClipboard(elRef.current.textContent || "");
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 800);
    } catch (e) {
      throw new Error("Failed to copy code to clipboard");
    }
  };

  return { justCopied, elRef, handleCopyToClipboard };
}
